import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Paper,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  Skeleton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  // eslint-disable-next-line no-unused-vars
  FormControlLabel,
  // eslint-disable-next-line no-unused-vars
  Switch,
  FormHelperText,
  // eslint-disable-next-line no-unused-vars
  Alert,
  Select,
  MenuItem,
  InputLabel,
  Menu,
  DialogContentText,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  People as PeopleIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  AdminPanelSettings as AdminIcon,
  Close as CloseIcon,
  RemoveCircleOutline as UnshareIcon,
} from "@mui/icons-material";
import DashboardLayout from "../components/DashboardLayout";
import ShareDialog from "../components/ShareDialog";
import { llmApi } from "../services/rag-service";
import { useAuth } from "../contexts/AuthContext";
import {
  isKeycloakAuthenticated,
  getKeycloakUserInfo,
} from "../services/keycloak";
import { toast } from "sonner";
import {
  useProjects,
  validateSelectedProjects,
} from "../contexts/ProjectContext";
import { PROJECT_SELECTION_CHANGED_EVENT } from "../components/ProjectSwitcher";
import { useNavigate } from "react-router-dom";
import ModelRegistrationForm from "../components/ModelRegistrationForm";
import { isUserAdmin } from "../utils/auth-helpers";
import { useTranslation } from "react-i18next";

// Get model provider color - moved outside components to be accessible by both
const getProviderColor = (provider) => {
  switch (provider.toLowerCase()) {
    case "openai":
      return "success";
    case "anthropic":
      return "info";
    case "cohere":
      return "secondary";
    case "mistral":
      return "primary";
    case "groq":
      return "warning";
    case "private":
      return "secondary";
    default:
      return "default";
  }
};

// Skeleton card component for loading state
const ModelSkeletonCard = () => {
  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="circular" width={24} height={24} />
        </Box>
        <Box sx={{ mt: 2, mb: 1 }}>
          <Skeleton
            variant="rounded"
            width={80}
            height={24}
            sx={{ marginInlineEnd: 1, display: "inline-block" }}
          />
          <Skeleton
            variant="rounded"
            width={80}
            height={24}
            sx={{ display: "inline-block" }}
          />
        </Box>
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="90%" height={20} />
        <Box sx={{ mt: 2 }}>
          <Skeleton variant="text" width="50%" height={20} />
        </Box>
      </CardContent>
    </Card>
  );
};

// ProviderIcon component to display provider icons
const ProviderIcon = ({ provider }) => {
  const providerMap = {
    openai: "/providers/openai.ico",
    anthropic: "/providers/anthropic.webp",
    cohere: "/providers/cohere.svg",
    mistral: "/providers/mistral.webp",
    groq: "/providers/groq.png",
  };

  // Normalize provider name (lowercase for comparison)
  const normalizedProvider = provider.toLowerCase();

  // Get the provider icon path or undefined if not available
  const iconPath = providerMap[normalizedProvider];

  if (!iconPath) {
    return (
      <Avatar
        sx={{
          width: 24,
          height: 24,
          bgcolor: getProviderColor(provider) + ".main",
        }}
      >
        {provider.charAt(0).toUpperCase()}
      </Avatar>
    );
  }

  return (
    <Avatar
      src={iconPath}
      alt={provider}
      sx={{
        width: 24,
        height: 24,
        border: "1px solid",
        borderColor: "divider",
      }}
      imgProps={{
        style: { objectFit: "contain" },
      }}
    />
  );
};

const AICatalog = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [projectSelectionDialogOpen, setProjectSelectionDialogOpen] =
    useState(false);
  // eslint-disable-next-line no-unused-vars
  const [formError, setFormError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false); // Track if user is admin

  // Add filter state
  const [viewFilter, setViewFilter] = useState("all"); // 'all', 'owned', 'shared'

  // Add state for model actions menu
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareSubmitting, setShareSubmitting] = useState(false);
  const [shareError, setShareError] = useState("");

  // Share users dialog state
  const [shareUsersDialogOpen, setShareUsersDialogOpen] = useState(false);
  const [shareUsers, setShareUsers] = useState([]);
  const [shareUsersLoading, setShareUsersLoading] = useState(false);

  // Unshare dialog state
  const [unshareDialogOpen, setUnshareDialogOpen] = useState(false);
  const [unshareLoading, setUnshareLoading] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState("");

  // Get authentication context
  const { currentUser, userProfile, authProvider } = useAuth();

  // Get projects context
  const { selectedProjects, availableProjects } = useProjects();

  // Use utility function to ensure we always have valid arrays with stable references
  const safeSelectedProjects = useMemo(
    () => validateSelectedProjects(selectedProjects),
    [selectedProjects],
  );
  const safeAvailableProjects = useMemo(
    () => (Array.isArray(availableProjects) ? availableProjects : []),
    [availableProjects],
  );

  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();

  // Helper function to get active projects
  const getActiveProjects = useCallback(() => {
    if (safeSelectedProjects.includes("all")) {
      return safeAvailableProjects;
    }
    return safeAvailableProjects.filter((project) =>
      safeSelectedProjects.includes(project.id),
    );
  }, [safeSelectedProjects, safeAvailableProjects]);

  // Define fetchModels with useCallback
  const fetchModels = useCallback(async () => {
    setLoading(true);
    try {
      // Add project_ids filter if available
      const options = {};

      // Add project_ids parameter if provided (and not 'all')
      if (safeSelectedProjects && !safeSelectedProjects.includes("all")) {
        options.project_ids = safeSelectedProjects;
      }

      // Add filter parameters based on viewFilter
      if (viewFilter === "all") {
        // All models: include public and shared models
        options.include_public = true;
        options.include_shared = true;
      } else if (viewFilter === "shared") {
        // Shared with me: only include shared models, not public ones
        options.include_public = false;
        options.include_shared = true;
      } else if (viewFilter === "owned") {
        // My models: only show owned models
        options.include_public = false;
        options.include_shared = false;
      }

      const response = await llmApi.getLLMConfigs(options);
      setModels(response.items);
    } catch (error) {
      console.error("Error fetching models:", error);
      toast.error(t("aiCatalog.failedToLoadModels"));
    } finally {
      setLoading(false);
    }
  }, [safeSelectedProjects, viewFilter]);

  // Fetch models and initialize data
  useEffect(() => {
    fetchModels();

    // Check if user is admin
    setIsAdmin(isUserAdmin());

    // Determine the current user's email based on authentication method
    if (authProvider === "keycloak" && isKeycloakAuthenticated()) {
      const keycloakUser = getKeycloakUserInfo();
      if (keycloakUser && keycloakUser.email) {
        setCurrentUserEmail(keycloakUser.email);
      }
    } else if (currentUser && currentUser.email) {
      setCurrentUserEmail(currentUser.email);
    } else if (userProfile && userProfile.email) {
      setCurrentUserEmail(userProfile.email);
    }
  }, [currentUser, userProfile, authProvider, fetchModels]);

  // Add listener for project selection changes
  useEffect(() => {
    const handleProjectChange = () => {
      fetchModels();
    };

    // Add event listener
    window.addEventListener(
      PROJECT_SELECTION_CHANGED_EVENT,
      handleProjectChange,
    );

    // Clean up
    return () => {
      window.removeEventListener(
        PROJECT_SELECTION_CHANGED_EVENT,
        handleProjectChange,
      );
    };
  }, [fetchModels]);

  const handleOpenDialog = () => {
    // Get the active projects
    const activeProjects = getActiveProjects();

    // If no projects are selected or available, show error
    if (activeProjects.length === 0) {
      toast.error(t("aiCatalog.pleaseSelectProject"));
      return;
    }

    // If 'all' is selected or multiple projects are selected, show project selection dialog
    if (
      safeSelectedProjects.includes("all") ||
      safeSelectedProjects.length > 1
    ) {
      // If all is selected but only one project is available, use it directly
      if (
        safeSelectedProjects.includes("all") &&
        safeAvailableProjects.length === 1
      ) {
        setSelectedProjectId(safeAvailableProjects[0].id);
        openModelRegistrationForm();
      } else {
        // Reset the selected project ID to ensure user makes a choice
        setSelectedProjectId("");
        // Open project selection dialog
        setProjectSelectionDialogOpen(true);
      }
    } else {
      // Only one specific project is selected, use it automatically
      setSelectedProjectId(safeSelectedProjects[0]);
      openModelRegistrationForm();
    }
  };

  const openModelRegistrationForm = () => {
    setOpenDialog(true);
    // No need to reset project ID here, as it's already set correctly
    setFormError("");
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    // Reset the project ID when closing the dialog
    setSelectedProjectId("");
  };

  const handleProjectSelectionClose = () => {
    setProjectSelectionDialogOpen(false);
  };

  const handleProjectSelectionConfirm = () => {
    if (!selectedProjectId) {
      toast.error(t("aiCatalog.pleaseSelectProject"));
      return;
    }

    // Close the project selection dialog
    setProjectSelectionDialogOpen(false);

    // Open the model registration form
    openModelRegistrationForm();
  };

  const handleProjectChange = (event) => {
    setSelectedProjectId(event.target.value);
  };

  // Handle menu open
  const handleMenuOpen = (event, model) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedModel(model);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Handle delete click from menu
  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  // Handle delete dialog close
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!selectedModel) return;

    setDeleteLoading(true);
    try {
      await llmApi.deleteLLMConfig(selectedModel.id);
      toast.success(t("aiCatalog.modelDeletedSuccess"));
      fetchModels(); // Refresh the models list
    } catch (error) {
      console.error("Error deleting model:", error);
      toast.error(t("aiCatalog.failedToDeleteModel"));
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  // Handle share click from menu
  const handleShareClick = () => {
    handleMenuClose();
    setShareDialogOpen(true);
    setShareError("");
  };

  // Handle share dialog close
  const handleShareCancel = () => {
    setShareDialogOpen(false);
  };

  // Handle share submit
  const handleShareSubmit = async (email, accessLevel) => {
    if (!selectedModel) return;

    setShareSubmitting(true);
    setShareError("");

    try {
      await llmApi.shareLLMConfig(selectedModel.id, {
        user: email,
        role: accessLevel,
      });

      toast.success(
        t("aiCatalog.modelSharedSuccess", { name: selectedModel.name, email }),
      );
      fetchModels(); // Refresh the models list
      setShareDialogOpen(false);
    } catch (error) {
      console.error("Error sharing model:", error);

      // Check for specific error message
      if (error.message.includes("User not found or inactive")) {
        setShareError(t("aiCatalog.userNotFoundError"));
      } else {
        setShareError(
          error.message || "Failed to share model. Please try again.",
        );
      }
    } finally {
      setShareSubmitting(false);
    }
  };

  // Access level styling and icons
  const getAccessLevelInfo = (accessLevel) => {
    switch (accessLevel) {
      case "admin":
        return {
          label: t("aiCatalog.admin"),
          icon: <AdminIcon fontSize="small" />,
          color: "primary",
        };
      case "modifier":
        return {
          label: t("aiCatalog.editor"),
          icon: <EditIcon fontSize="small" />,
          color: "success",
        };
      case "viewer":
        return {
          label: t("aiCatalog.viewer"),
          icon: <VisibilityIcon fontSize="small" />,
          color: "default",
        };
      default:
        return {
          label: "Access",
          icon: <VisibilityIcon fontSize="small" />,
          color: "default",
        };
    }
  };

  // Handle view shares click from menu
  const handleViewSharesClick = async () => {
    if (!selectedModel) return;

    handleMenuClose();
    setShareUsersDialogOpen(true);
    setShareUsers([]);
    setShareUsersLoading(true);

    try {
      const users = await llmApi.getLLMConfigShares(selectedModel.id);
      setShareUsers(users);
    } catch (error) {
      console.error("Error fetching shared users:", error);
      toast.error(t("aiCatalog.failedToLoadSharedUsers"));
    } finally {
      setShareUsersLoading(false);
    }
  };

  // Handle share users dialog close
  const handleShareUsersDialogClose = () => {
    setShareUsersDialogOpen(false);
  };

  // Handle unshare click from menu
  const handleUnshareClick = () => {
    handleMenuClose();
    setUnshareDialogOpen(true);
  };

  // Handle unshare dialog close
  const handleUnshareCancel = () => {
    setUnshareDialogOpen(false);
  };

  // Handle unshare confirmation
  const handleUnshareConfirm = async () => {
    if (!selectedModel) return;

    // Determine the email to use for unsharing
    let emailToUse = currentUserEmail;

    // If email isn't set yet, try to get it from Keycloak first, then fallback to other sources
    if (
      !emailToUse &&
      authProvider === "keycloak" &&
      isKeycloakAuthenticated()
    ) {
      const keycloakUser = getKeycloakUserInfo();
      emailToUse = keycloakUser?.email || "";
    } else if (!emailToUse && currentUser) {
      emailToUse = currentUser.email || "";
    } else if (!emailToUse && userProfile) {
      emailToUse = userProfile.email || "";
    }

    if (!emailToUse) {
      toast.error(
        "Unable to determine your email address. Please reload the page or contact support.",
      );
      setUnshareDialogOpen(false);
      return;
    }

    setUnshareLoading(true);
    try {
      await llmApi.unshareByEmail(selectedModel.id, emailToUse);
      toast.success(t("aiCatalog.modelRemovedSuccess"));
      fetchModels(); // Refresh the models list
    } catch (error) {
      console.error("Error unsharing model:", error);
      toast.error(t("aiCatalog.failedToRemoveModel"));
    } finally {
      setUnshareLoading(false);
      setUnshareDialogOpen(false);
    }
  };

  // Handle remove user share
  const handleRemoveUserShare = async (userEmail) => {
    if (!selectedModel || !userEmail) return;

    try {
      await llmApi.unshareByEmail(selectedModel.id, userEmail);
      toast.success(t("aiCatalog.userAccessRemovedSuccess"));

      // Refresh the shares list
      handleViewSharesClick();
    } catch (error) {
      console.error("Error removing user access:", error);
      toast.error(t("aiCatalog.failedToRemoveUserAccess"));
    }
  };

  // Add a handler for view filter changes
  const handleViewFilterChange = (event) => {
    setViewFilter(event.target.value);
    // No need to call fetchModels manually as it will be triggered by the dependency change
  };

  // Filter models based on search term and view filter
  const filteredModels = models.filter((model) => {
    // Apply search filter only - backend already handles the view filter
    return (
      model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.provider.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.model.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <DashboardLayout title={t("aiCatalog.title")}>
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <Typography variant="h4" component="h1" gutterBottom>
              {t("aiCatalog.heading")}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t("aiCatalog.subtitle")}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{
                minWidth: { xs: "100%", sm: "100%", md: "auto", lg: "auto" },
              }}
            >
              {t("aiCatalog.registerModel")}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Paper sx={{ p: 2, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <TextField
              placeholder={t("aiCatalog.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="view-filter-label">{t("common.view")}</InputLabel>
              <Select
                labelId="view-filter-label"
                id="view-filter"
                value={viewFilter}
                onChange={handleViewFilterChange}
                label={t("common.view")}
              >
                <MenuItem value="all">{t("aiCatalog.allModels")}</MenuItem>
                <MenuItem value="owned">{t("aiCatalog.myModels")}</MenuItem>
                <MenuItem value="shared">
                  {t("aiCatalog.sharedWithMe")}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <IconButton onClick={fetchModels} title={t("common.refresh")}>
              <RefreshIcon className="rtl:scale-x-[-1]" />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Grid container spacing={3}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid item xs={12} md={6} lg={4} key={`skeleton-${index}`}>
              <ModelSkeletonCard />
            </Grid>
          ))}
        </Grid>
      ) : filteredModels.length > 0 ? (
        <Grid container spacing={3}>
          {filteredModels.map((model) => (
            <Grid item xs={12} md={6} lg={4} key={model.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
                  },
                  position: "relative",
                  overflow: "visible",
                }}
              >
                {/* Shared indicator */}
                {model.is_shared && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -8,
                      insetInlineEnd: 16,
                      transform: "rotate(0)",
                      zIndex: 1,
                    }}
                  >
                    <Chip
                      icon={<ShareIcon fontSize="small" />}
                      label={t("aiCatalog.shared")}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{
                        fontWeight: "bold",
                        border: "1px solid",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      }}
                    />
                  </Box>
                )}

                <CardContent
                  sx={{
                    flexGrow: 1,
                    p: 2,
                    pt: model.is_shared ? 3 : 2, // Give more top padding if shared to avoid overlap
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "calc(100% - 60px)", // Leave space for icons
                        fontSize: "1.1rem", // Slightly smaller font size
                      }}
                    >
                      {model.name}
                    </Typography>
                    {/* Show the three dots menu for model owners, users with admin/modifier access, or global admins */}
                    {((!model.is_public &&
                      (model.access_level === "admin" ||
                        model.access_level === "modifier" ||
                        model.is_owner)) ||
                      isAdmin) && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, model)}
                        aria-label="model options"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    )}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={
                        model.is_public
                          ? t("aiCatalog.public")
                          : t("aiCatalog.private")
                      }
                      color={model.is_public ? "primary" : "default"}
                      size="small"
                      sx={{ marginInlineEnd: 1 }}
                    />
                    <Chip
                      label={
                        model.is_embedding_model
                          ? t("aiCatalog.embeddingModel")
                          : model.is_stt_model
                            ? t("aiCatalog.speechToText")
                            : t("aiCatalog.textGeneration")
                      }
                      color={
                        model.is_embedding_model
                          ? "info"
                          : model.is_stt_model
                            ? "warning"
                            : "success"
                      }
                      size="small"
                    />
                    {model.is_multimodal && (
                      <Chip
                        label={t("aiCatalog.multimodal")}
                        size="small"
                        sx={{
                          marginInlineStart: 1,
                          bgcolor: "rgba(156, 39, 176, 0.12)",
                          color: "var(--color-67)",
                          border: "1px solid rgba(156, 39, 176, 0.2)",
                          fontWeight: 600,
                        }}
                      />
                    )}
                    {model.is_default && (
                      <Chip
                        label={t("aiCatalog.default")}
                        color="warning"
                        size="small"
                        sx={{ marginInlineStart: 1 }}
                      />
                    )}
                  </Box>

                  {/* Access level indicator */}
                  {model.is_shared && model.access_level && (
                    <Box sx={{ mb: 1.5 }}>
                      {(() => {
                        const accessInfo = getAccessLevelInfo(
                          model.access_level,
                        );
                        return (
                          <Chip
                            avatar={
                              <Avatar sx={{ bgcolor: "transparent" }}>
                                {accessInfo.icon}
                              </Avatar>
                            }
                            label={accessInfo.label}
                            size="small"
                            color={accessInfo.color}
                            variant="outlined"
                            sx={{ height: 24 }}
                          />
                        );
                      })()}
                    </Box>
                  )}

                  <Divider sx={{ mb: 2 }} />

                  {/* Display owner information for shared models */}
                  {model.is_shared && model.owner_name && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t("aiCatalog.sharedBy")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <ShareIcon
                          fontSize="small"
                          sx={{ marginInlineEnd: 0.5, color: "primary.main" }}
                        />
                        {model.owner_name}
                      </Typography>
                    </Box>
                  )}

                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t("aiCatalog.provider")}
                      </Typography>
                      <Box
                        sx={{ mt: 0.5, display: "flex", alignItems: "center" }}
                      >
                        <ProviderIcon provider={model.provider} />
                        <Typography
                          variant="body2"
                          sx={{
                            marginInlineStart: 1,
                            fontWeight: "medium",
                            color: `${getProviderColor(model.provider)}.main`,
                          }}
                        >
                          {model.provider}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t("aiCatalog.model")}
                      </Typography>
                      <Typography variant="body2">{model.model}</Typography>
                    </Grid>
                  </Grid>

                  {/* Show different fields based on model type */}
                  {model.is_embedding_model ? (
                    // Embedding model: Only show embedding dimension
                    model.embedding_dimension && (
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            {t("aiCatalog.embeddingDimension")}
                          </Typography>
                          <Typography variant="body2">
                            {model.embedding_dimension}
                          </Typography>
                        </Grid>
                      </Grid>
                    )
                  ) : model.is_stt_model ? (
                    // STT model: Show relevant STT parameters
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t("aiCatalog.temperature")}
                        </Typography>
                        <Typography variant="body2">
                          {model.temperature}
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t("aiCatalog.maxTokens")}
                        </Typography>
                        <Typography variant="body2">
                          {model.max_tokens > 0
                            ? model.max_tokens
                            : t("common.unlimited")}
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    // Text generation model: Show temperature and max tokens
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t("aiCatalog.temperature")}
                        </Typography>
                        <Typography variant="body2">
                          {model.temperature}
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t("aiCatalog.maxTokens")}
                        </Typography>
                        <Typography variant="body2">
                          {model.max_tokens > 0
                            ? model.max_tokens
                            : t("common.unlimited")}
                        </Typography>
                      </Grid>
                    </Grid>
                  )}

                  {model.api_key_required && (
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        label={t("aiCatalog.apiKeyRequired")}
                        color="error"
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary" paragraph>
            {searchTerm
              ? t("aiCatalog.noMatchingModels")
              : t("aiCatalog.noModelsAvailable")}
          </Typography>
        </Paper>
      )}

      {/* Model Registration Dialog */}
      <ModelRegistrationForm
        open={openDialog}
        onClose={handleCloseDialog}
        projectId={selectedProjectId}
        onSuccess={fetchModels}
      />

      {/* Model Options Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {/* Only show share option if the user has admin access level */}
        {selectedModel &&
          (selectedModel.is_owner ||
            selectedModel.access_level === "admin" ||
            isAdmin) && (
            <MenuItem onClick={handleShareClick}>
              <ShareIcon fontSize="small" sx={{ marginInlineEnd: 1 }} />
              <Typography>{t("common.share")}</Typography>
            </MenuItem>
          )}
        {/* Show view shares option if the user is the owner */}
        {selectedModel && (selectedModel.is_owner || isAdmin) && (
          <MenuItem onClick={handleViewSharesClick}>
            <PeopleIcon fontSize="small" sx={{ marginInlineEnd: 1 }} />
            <Typography>{t("aiCatalog.manageShares")}</Typography>
          </MenuItem>
        )}
        {/* Show delete option for owners/admins, or unshare for shared models */}
        {selectedModel &&
          (selectedModel.is_owner ||
          selectedModel.access_level === "admin" ||
          isAdmin ? (
            <MenuItem onClick={handleDeleteClick}>
              <DeleteIcon
                fontSize="small"
                sx={{ marginInlineEnd: 1, color: "error.main" }}
              />
              <Typography color="error">{t("common.delete")}</Typography>
            </MenuItem>
          ) : selectedModel.is_shared ? (
            <MenuItem onClick={handleUnshareClick}>
              <UnshareIcon
                fontSize="small"
                sx={{ marginInlineEnd: 1, color: "error.main" }}
              />
              <Typography color="error">
                {t("aiCatalog.removeFromMyModels")}
              </Typography>
            </MenuItem>
          ) : null)}
      </Menu>

      {/* Share Dialog - using new component */}
      <ShareDialog
        open={shareDialogOpen}
        onClose={handleShareCancel}
        onSubmit={handleShareSubmit}
        title={t("aiCatalog.shareModelTitle", {
          name: selectedModel?.name || "",
        })}
        itemType="Model"
        isSubmitting={shareSubmitting}
        error={shareError}
        projectId={selectedModel?.project_id}
      />

      {/* Shared Users Dialog */}
      <Dialog
        open={shareUsersDialogOpen}
        onClose={handleShareUsersDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PeopleIcon sx={{ marginInlineEnd: 1 }} />
            {t("aiCatalog.usersWithAccess", { name: selectedModel?.name })}
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleShareUsersDialogClose}
            disabled={shareUsersLoading}
            sx={{
              position: "absolute",
              insetInlineEnd: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {shareUsersLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : shareUsers.length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography color="text.secondary">
                {t("aiCatalog.notSharedYet")}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={handleShareClick}
                sx={{ mt: 2 }}
              >
                {t("aiCatalog.shareWithUser")}
              </Button>
            </Box>
          ) : (
            <Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("aiCatalog.user")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("aiCatalog.email")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("aiCatalog.accessLevel")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shareUsers.map((user) => {
                    const accessInfo = getAccessLevelInfo(user.access_level);
                    return (
                      <TableRow key={user.id}>
                        <TableCell align={isRTL ? "right" : "left"}>
                          {user.user_name || t("common.unknown")}
                        </TableCell>
                        <TableCell align={isRTL ? "right" : "left"}>
                          {user.user_email}
                        </TableCell>
                        <TableCell align={isRTL ? "right" : "left"}>
                          <Chip
                            avatar={
                              <Avatar sx={{ bgcolor: "transparent" }}>
                                {accessInfo.icon}
                              </Avatar>
                            }
                            label={accessInfo.label}
                            color={accessInfo.color}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align={isRTL ? "right" : "left"}>
                          <Tooltip title={t("aiCatalog.removeAccess")}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleRemoveUserShare(user.user_email)
                              }
                              disabled={shareUsersLoading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {shareUsers.length > 0 && (
            <Button
              onClick={handleShareClick}
              startIcon={<ShareIcon />}
              sx={{ marginInlineEnd: "auto", color: "text.primary" }}
            >
              {t("aiCatalog.shareWithAnotherUser")}
            </Button>
          )}
          <Button
            onClick={handleShareUsersDialogClose}
            sx={{ color: "text.primary" }}
          >
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unshare Confirmation Dialog */}
      <Dialog open={unshareDialogOpen} onClose={handleUnshareCancel}>
        <DialogTitle>
          {t("aiCatalog.removeModel")}
          <IconButton
            aria-label="close"
            onClick={handleUnshareCancel}
            disabled={unshareLoading}
            sx={{
              position: "absolute",
              insetInlineEnd: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("aiCatalog.removeModelConfirm", { name: selectedModel?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleUnshareCancel}
            disabled={unshareLoading}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleUnshareConfirm}
            color="error"
            variant="contained"
            disabled={unshareLoading}
            startIcon={
              unshareLoading ? <CircularProgress size={20} /> : <UnshareIcon />
            }
          >
            {unshareLoading ? t("common.removing") : t("common.remove")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t("aiCatalog.deleteModel")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("aiCatalog.deleteModelConfirm", { name: selectedModel?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleteLoading}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            startIcon={
              deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />
            }
          >
            {deleteLoading ? t("common.deleting") : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Project Selection Dialog */}
      <Dialog
        open={projectSelectionDialogOpen}
        onClose={handleProjectSelectionClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("aiCatalog.selectProject")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("aiCatalog.selectProjectDescription")}
          </DialogContentText>

          <FormControl fullWidth margin="normal">
            <InputLabel id="project-select-label">
              {t("flows.project")}
            </InputLabel>
            <Select
              labelId="project-select-label"
              id="project-select"
              value={selectedProjectId}
              onChange={handleProjectChange}
              label="Project"
            >
              {getActiveProjects().map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {t("aiCatalog.selectProjectHelper")}
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleProjectSelectionClose}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleProjectSelectionConfirm}
            variant="contained"
            color="primary"
            disabled={!selectedProjectId}
          >
            {t("common.continue")}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default AICatalog;
